import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import {
	usePricebookAddOnsForNewSubscriptionQuery,
	usePricebookDiscountTiersQuery,
	usePricebookProductsQuery,
	useSearchPricebookItemsMutation,
} from '../../../../queries/Admin/Pricebook';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
} from '../../../../web/components-v2/Combobox';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { TaxEstimateDisplay } from '../TaxEstimateDisplay';
import { TaxEstimateModal } from '../TaxEstimateModal';
import { GenerateContractModal } from './GenerateContractModal';
import { GenerateProposalModal } from './GenerateProposalModal';
import { styleSheet } from './styles';
import {
	PricebookDiscountTier,
	PricebookItem,
	PricebookProduct,
	PricingCalculationResponse,
	TaxEstimateData,
} from './types';

// Constants for Additional Users dropdown
// 0-10 in increments of 1, then 15-50 in increments of 5
const ADDITIONAL_USERS_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 35, 40, 45, 50];

// Billing cycle options
const BILLING_CYCLE_OPTIONS = [
	{ value: 12, label: 'Annual' },
	{ value: 3, label: 'Quarterly' },
];

// AI Solutions product detection. Products are categorized server-side as 'AI Solutions';
// the productCode also carries an implementation-fee suffix (e.g. 299-3-AI-I1000) as a fallback.
const AI_SOLUTIONS_CATEGORY = 'AI Solutions';
const AI_PRODUCT_CODE_SUFFIX = '-AI-I';

// AI Solutions products generate no offers for the Meta/LSA/PPC/Handwritten add-ons,
// so those toggles must be off (any of them on yields an empty search).
function isAiSolutionsProduct(product: PricebookProduct | null): boolean {
	if (!product) return false;
	return product.category === AI_SOLUTIONS_CATEGORY || product.productCode?.includes(AI_PRODUCT_CODE_SUFFIX);
}

// Hover tooltip text explaining why an add-on toggle is disabled, or undefined when it's available.
function getAddOnUnavailableReason(available: boolean, aiProductSelected: boolean): string | undefined {
	if (aiProductSelected) return 'Not available for AI Solutions';
	if (!available) return 'Not available for this billing cycle';
	return undefined;
}

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	accountId?: string | null;
}

export function PricingWizard({ className, styles: customStyles, accountId }: IProps) {
	const userSession = useAdminUserSession();
	const history = useHistory();

	// API queries for pricebook data
	const productsQuery = usePricebookProductsQuery();
	const addOnsQuery = usePricebookAddOnsForNewSubscriptionQuery();
	const discountTiersQuery = usePricebookDiscountTiersQuery();
	const searchPricebookMutation = useSearchPricebookItemsMutation();

	// Form state
	const [selectedProduct, setSelectedProduct] = React.useState<PricebookProduct | null>(null);
	const [productSearchValue, setProductSearchValue] = React.useState('');
	const [selectedBillingCycle, setSelectedBillingCycle] = React.useState<number>(12); // Default to Annual
	const [billingCycleSearchValue, setBillingCycleSearchValue] = React.useState('Annual');
	const [selectedDiscount, setSelectedDiscount] = React.useState<PricebookDiscountTier | null>(null);
	const [discountSearchValue, setDiscountSearchValue] = React.useState('');
	const [additionalUsers, setAdditionalUsers] = React.useState<number>(0);
	const [additionalUsersSearchValue, setAdditionalUsersSearchValue] = React.useState('0');

	// Add-on checkboxes
	const [hasMetaAdsPremium, setHasMetaAdsPremium] = React.useState(false);
	const [hasGoogleLSA, setHasGoogleLSA] = React.useState(false);
	const [hasGooglePpc, setHasGooglePpc] = React.useState(false);
	const [hasHandwrittenCards, setHasHandwrittenCards] = React.useState(false);
	const [isSplitBilling, setIsSplitBilling] = React.useState(false);
	const [payByCheck, setPayByCheck] = React.useState(false);

	// Results state
	const [result, setResult] = React.useState<PricingCalculationResponse | null>(null);
	const [pricebookItem, setPricebookItem] = React.useState<PricebookItem | null>(null);

	// Modal state
	const [isProposalModalOpen, setIsProposalModalOpen] = React.useState(false);
	const [isNewContractModalOpen, setIsNewContractModalOpen] = React.useState(false);
	const [isCurrentContractModalOpen, setIsCurrentContractModalOpen] = React.useState(false);
	const [isNoMatchModalOpen, setIsNoMatchModalOpen] = React.useState(false);
	const [isTaxModalOpen, setIsTaxModalOpen] = React.useState(false);

	// Tax estimate state
	const [taxEstimate, setTaxEstimate] = React.useState<TaxEstimateData | null>(null);

	// Check permissions - Success team has ModifyAccount, Sales team does not
	const isSuccessTeam = userSession.hasPermission(AdminPermission.ModifyAccount);

	// Filter products by selected billing cycle
	const filteredProducts = React.useMemo(() => {
		if (!productsQuery.data) return [];
		return productsQuery.data.filter(p => p.billingCycle === selectedBillingCycle);
	}, [productsQuery.data, selectedBillingCycle]);

	// Filter add-ons by selected billing cycle
	const filteredAddOns = React.useMemo(() => {
		if (!addOnsQuery.data) return [];
		return addOnsQuery.data.filter(a => a.billingCycle === selectedBillingCycle);
	}, [addOnsQuery.data, selectedBillingCycle]);

	// Check if add-ons are available for current billing cycle
	const metaAdsPremiumAvailable = React.useMemo(
		() => filteredAddOns.some(a => a.name.toLowerCase().includes('meta ads premium')),
		[filteredAddOns]
	);
	// Exclude PPC matches so a "Google PPC" add-on doesn't falsely enable the LSA checkbox.
	const googleLSAAvailable = React.useMemo(
		() =>
			filteredAddOns.some(a => {
				const name = a.name.toLowerCase();
				return (
					(name.includes('google') || name.includes('lsa')) &&
					!name.includes('ppc') &&
					!name.includes('pay-per-click') &&
					!name.includes('pay per click')
				);
			}),
		[filteredAddOns]
	);
	const googlePpcAvailable = React.useMemo(
		() =>
			filteredAddOns.some(a => {
				const name = a.name.toLowerCase();
				return name.includes('ppc') || name.includes('pay-per-click') || name.includes('pay per click');
			}),
		[filteredAddOns]
	);
	const handwrittenCardsAvailable = React.useMemo(
		() => filteredAddOns.some(a => a.name.toLowerCase().includes('handwritten')),
		[filteredAddOns]
	);

	// AI Solutions products don't support the Meta/LSA/PPC/Handwritten offer toggles.
	const aiProductSelected = isAiSolutionsProduct(selectedProduct);

	// Force the unsupported toggles off when an AI Solutions product is selected.
	React.useEffect(() => {
		if (aiProductSelected) {
			setHasMetaAdsPremium(false);
			setHasGoogleLSA(false);
			setHasGooglePpc(false);
			setHasHandwrittenCards(false);
		}
	}, [aiProductSelected]);

	// Reset product selection when billing cycle changes, but try to match by name first
	React.useEffect(() => {
		if (selectedProduct && selectedProduct.billingCycle !== selectedBillingCycle) {
			// Try to find a matching product by name in the new billing cycle
			const matchingProduct = filteredProducts.find(p => p.name === selectedProduct.name);
			if (matchingProduct) {
				setSelectedProduct(matchingProduct);
				setProductSearchValue(matchingProduct.name);
			} else {
				setSelectedProduct(null);
				setProductSearchValue('');
			}
		}
		// Reset unavailable add-ons
		if (!metaAdsPremiumAvailable) setHasMetaAdsPremium(false);
		if (!googleLSAAvailable) setHasGoogleLSA(false);
		if (!googlePpcAvailable) setHasGooglePpc(false);
		if (!handwrittenCardsAvailable) setHasHandwrittenCards(false);
		// Split billing only available for annual
		if (selectedBillingCycle !== 12) setIsSplitBilling(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		selectedBillingCycle,
		metaAdsPremiumAvailable,
		googleLSAAvailable,
		googlePpcAvailable,
		handwrittenCardsAvailable,
	]);

	const handleCalculate = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedProduct) {
			toast.error('Please select a product');
			return;
		}

		// Reset tax estimate when recalculating (pricebookItem changes)
		setTaxEstimate(null);

		searchPricebookMutation.mutate(
			{
				productCode: selectedProduct.productCode,
				discount: selectedDiscount ? selectedDiscount.percentage.toString() : undefined,
				hasFacebookPremium: hasMetaAdsPremium.toString(),
				hasHandwrittenCards: hasHandwrittenCards.toString(),
				hasGoogleLocalServiceAds: hasGoogleLSA.toString(),
				hasGooglePpc: hasGooglePpc.toString(),
				additionalUsers: additionalUsers.toString(),
				isSplitBilling: isSplitBilling.toString(),
			},
			{
				onError: error => {
					toast.error(error.systemMessage ?? 'Failed to calculate pricing');
				},
				onSuccess: items => {
					if (!items || items.length === 0) {
						setIsNoMatchModalOpen(true);
						setResult(null);
						setPricebookItem(null);
						return;
					}

					const item = items[0];
					setPricebookItem(item);

					// Helper to get component price from chargifyOfferComponents
					const getComponentPrice = (searchTerm: string): number => {
						const component = item.chargifyOfferComponents?.find(c =>
							c.name?.toLowerCase().includes(searchTerm.toLowerCase())
						);
						return component?.unitPrice ?? 0;
					};

					// Build plan addons string
					const addons: string[] = [];
					if (item.hasGoogleLocalServiceAds) addons.push('Google LSA');
					if (item.hasGooglePpc) addons.push('Google PPC');
					if (item.hasFacebookAdsPremium) addons.push('Meta Ads Premium');
					if (item.hasHandwrittenCards) addons.push('Handwritten Cards');

					const planAddons =
						addons.length === 0
							? ''
							: addons.length === 1
								? addons[0]
								: addons.length === 2
									? addons.join(' & ')
									: addons.slice(0, -1).join(', ') + ' & ' + addons[addons.length - 1];

					const billingCycleLabel =
						selectedBillingCycle === 12 ? 'Annual' : selectedBillingCycle === 3 ? 'Quarter' : 'Monthly';
					const quarterlyVerbiage = selectedBillingCycle === 3 ? '1 of 4 quarterly payments due today' : '';
					const splitBillingVerbiage = item.isSplitBilling
						? '1 of 2 payments due upon signup - 2nd payment due 60 days after'
						: '';
					const splitBillingCost = item.isSplitBilling ? item.totalFirstYearCostAfterDiscount / 2 : 0;

					// Calculate MRR values (convert billing-cycle prices to monthly)
					// unitPrice is for the billing cycle, so divide by billingCycle to get monthly
					const billingCycle = item.productBillingCycle;
					const extraUserCostMRR =
						item.additionalUsers > 0 ? (getComponentPrice('additional user') / billingCycle) * item.additionalUsers : 0;
					const facebookAdsMRR = item.hasFacebookAdsPremium
						? (getComponentPrice('meta ads premium') ||
								getComponentPrice('facebook ads premium') ||
								getComponentPrice('facebook premium')) / billingCycle
						: 0;
					const googleLSAMRR = item.hasGoogleLocalServiceAds ? getComponentPrice('google lsa') / billingCycle : 0;
					const googlePpcUnit = item.hasGooglePpc
						? getComponentPrice('pay-per-click') || getComponentPrice('pay per click') || getComponentPrice('ppc')
						: 0;
					if (item.hasGooglePpc && googlePpcUnit === 0) {
						// Surface misconfiguration: the item has PPC but we couldn't find the component price.
						console.warn('Google PPC is enabled on the pricebook item but no matching component price was found.');
					}
					const googlePpcMRR = googlePpcUnit / billingCycle;
					const handwrittenCardsMRR = item.hasHandwrittenCards ? getComponentPrice('handwritten') / billingCycle : 0;

					// Map PricebookItem to PricingCalculationResponse for compatibility with modals
					// All pricing breakdown values are ANNUALIZED so they add up to subtotal correctly
					const resultData: PricingCalculationResponse = {
						// Pricing breakdown (annualized)
						basePrice: item.productMRR * 12,
						extraUserCost: extraUserCostMRR * 12,
						onboardingFee: item.implementationPrice,
						facebookAdsFee: facebookAdsMRR * 12,
						googleLSAFee: googleLSAMRR * 12,
						googlePpcFee: googlePpcMRR * 12,
						handwrittenCardsFee: handwrittenCardsMRR * 12,
						// MRR values for display and contract generation (actual pricebook values)
						basePriceMRR: item.productMRR,
						extraUserCostMRR,
						facebookAdsMRR,
						googleLSAMRR,
						googlePpcMRR,
						handwrittenCardsMRR,
						subtotal: item.totalFirstYearCostBeforeDiscount,
						discountAmount: item.totalFirstYearCostBeforeDiscount - item.totalFirstYearCostAfterDiscount,
						totalBeforeTaxes: item.totalFirstYearCostAfterDiscount,
						taxAmount: 0, // Will be updated by tax estimation
						annualTotal: item.totalFirstYearCostAfterDiscount, // True annual total after discount
						quarterlyTotal: item.totalFirstYearCostAfterDiscount / 4,
						splitTotal: item.totalFirstYearCostAfterDiscount / 2,
						renewalAnnualTotal: item.totalMRR * 12,
						renewalQuarterlyTotal: item.totalMRR * 3,
						mrrAmount: item.totalMRR,
						offerUrl: item.offerSignupUrl || undefined,

						// Form fields for proposal/contract generation
						planType: item.productName,
						numUsers: (item.productName === 'Levitate Corporate' ? 10 : 5) + item.additionalUsers,
						billingCycle: billingCycleLabel,
						state: taxEstimate?.state || ' ',
						discountPercentage: selectedDiscount ? selectedDiscount.percentage * 100 : 0,
						fbAdsChecked: false, // Legacy - removed
						fbAdsPremiumChecked: item.hasFacebookAdsPremium,
						gLSAChecked: item.hasGoogleLocalServiceAds,
						googlePpcChecked: item.hasGooglePpc,
						customContentChecked: false, // Legacy - removed
						customContentNonprofit: false, // Legacy - removed
						handwritten: item.hasHandwrittenCards,
						splitBilling: item.isSplitBilling,

						// Calculated fields
						planAddons,
						pricingMonthly: item.totalMRR,
						impFeePrice: item.implementationPrice,
						pricingAnnual: item.totalFirstYearCostBeforeDiscount,
						totalCost: item.totalFirstYearCostAfterDiscount, // True annual total after discount
						quarterlyCost: item.totalFirstYearCostAfterDiscount / 4,
						quarterlyVerbiage,
						splitBillingVerbiage,
						splitBillingCost,
						discountSubtotal: item.totalFirstYearCostBeforeDiscount - item.totalFirstYearCostAfterDiscount,
					};

					setResult(resultData);
				},
			}
		);
	};

	const formatCurrency = (value: number | string | undefined): string => {
		const numericValue = typeof value === 'string' ? parseFloat(value) || 0 : (value ?? 0);
		const hasCents = numericValue % 1 !== 0;
		return numericValue.toLocaleString('en-US', {
			maximumFractionDigits: hasCents ? 2 : 0,
			minimumFractionDigits: hasCents ? 2 : 0,
		});
	};

	const renderResultRow = (
		label: string,
		value: number | undefined,
		options?: { shouldShow?: boolean; monthlyValue?: number }
	) => {
		if (options?.shouldShow === false) return null;

		const numeric = value ?? 0;
		const optionalFees = ['Meta Ads Premium Fee:', 'Google LSA Fee:', 'Google PPC Fee:', 'Handwritten Cards Fee:'];

		if (options?.shouldShow === undefined && numeric === 0 && optionalFees.includes(label)) {
			return null;
		}

		const monthlyDisplay =
			options?.monthlyValue !== undefined && options.monthlyValue > 0
				? `($${formatCurrency(options.monthlyValue)}/mo)`
				: '';

		return (
			<div className={css(styleSheet.resultRow)} key={label}>
				<span className={css(styleSheet.resultLabel)}>{label}</span>
				<span className={css(styleSheet.resultValue)}>
					{monthlyDisplay && <span className={css(styleSheet.monthlyNote)}>{monthlyDisplay}</span>}$
					{formatCurrency(numeric)}
				</span>
			</div>
		);
	};

	const isLoading = productsQuery.isLoading || addOnsQuery.isLoading || discountTiersQuery.isLoading;
	const isCalculating = searchPricebookMutation.isPending;

	if (isLoading) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='small' />
				<span>Loading pricing data...</span>
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container, ...(customStyles || []))} ${className || ''}`}>
			<div className={css(styleSheet.layout)}>
				{/* Left: Form Inputs */}
				<form className={css(styleSheet.formSection)} onSubmit={handleCalculate}>
					{/* Billing Cycle */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)}>Billing Cycle</label>
						<Combobox
							items={BILLING_CYCLE_OPTIONS}
							value={BILLING_CYCLE_OPTIONS.find(o => o.value === selectedBillingCycle) || null}
							onValueChange={option => {
								const opt = option as { value: number; label: string } | null;
								if (opt) {
									setSelectedBillingCycle(opt.value);
									setBillingCycleSearchValue(opt.label);
								}
							}}
							inputValue={billingCycleSearchValue}
							onInputValueChange={setBillingCycleSearchValue}
							itemToStringLabel={option => (option as { value: number; label: string }).label}
							isItemEqualToValue={(a, b) => (a as { value: number }).value === (b as { value: number }).value}
						>
							<ComboboxInput placeholder='Select billing cycle...' />
							<ComboboxContent>
								<ComboboxEmpty>No billing cycles found.</ComboboxEmpty>
								<ComboboxList>
									{(option: { value: number; label: string }) => (
										<ComboboxItem key={option.value} value={option}>
											<ComboboxItemRadioIndicator />
											{option.label}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>

					{/* Product */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)}>Product</label>
						<Combobox
							items={filteredProducts}
							value={selectedProduct}
							onValueChange={product => {
								const p = product as PricebookProduct | null;
								setSelectedProduct(p);
								if (p) setProductSearchValue(p.name);
							}}
							inputValue={productSearchValue}
							onInputValueChange={setProductSearchValue}
							itemToStringLabel={product => (product as PricebookProduct).name}
							isItemEqualToValue={(a, b) => (a as PricebookProduct).productCode === (b as PricebookProduct).productCode}
						>
							<ComboboxInput placeholder='Search products...' showClear />
							<ComboboxContent styles={[styleSheet.productDropdown]}>
								<ComboboxStatus>
									{filteredProducts.length === 0
										? 'No products available for this billing cycle'
										: `${filteredProducts.length} product${filteredProducts.length === 1 ? '' : 's'} available`}
								</ComboboxStatus>
								<ComboboxList>
									{(product: PricebookProduct) => (
										<ComboboxItem key={product.productCode} value={product}>
											<ComboboxItemRadioIndicator />
											{product.name} (${product.mrr}/mo)
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>

					{/* Discount */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)}>Discount</label>
						<Combobox
							items={discountTiersQuery.data || []}
							value={selectedDiscount}
							onValueChange={discount => {
								const d = discount as PricebookDiscountTier | null;
								setSelectedDiscount(d);
								if (d) setDiscountSearchValue(`${d.percentage * 100}%`);
							}}
							inputValue={discountSearchValue}
							onInputValueChange={setDiscountSearchValue}
							itemToStringLabel={discount => `${(discount as PricebookDiscountTier).percentage * 100}%`}
							isItemEqualToValue={(a, b) =>
								(a as PricebookDiscountTier).percentage === (b as PricebookDiscountTier).percentage
							}
						>
							<ComboboxInput placeholder='Select discount...' showClear />
							<ComboboxContent>
								<ComboboxEmpty>No discount tiers found.</ComboboxEmpty>
								<ComboboxList>
									{(discount: PricebookDiscountTier) => (
										<ComboboxItem key={discount.percentage} value={discount}>
											<ComboboxItemRadioIndicator />
											{discount.percentage * 100}%
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>

					{/* Additional Users */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)}>Additional Users</label>
						<Combobox
							items={ADDITIONAL_USERS_OPTIONS}
							value={additionalUsers}
							onValueChange={users => {
								const u = users as number | null;
								if (u !== null) {
									setAdditionalUsers(u);
									setAdditionalUsersSearchValue(String(u));
								}
							}}
							inputValue={additionalUsersSearchValue}
							onInputValueChange={setAdditionalUsersSearchValue}
							itemToStringLabel={users => String(users as number)}
							isItemEqualToValue={(a, b) => (a as number) === (b as number)}
						>
							<ComboboxInput placeholder='Select additional users...' />
							<ComboboxContent>
								<ComboboxList>
									{(users: number) => (
										<ComboboxItem key={users} value={users}>
											<ComboboxItemRadioIndicator />
											{users} additional user{users !== 1 ? 's' : ''}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>

					{/* Split Billing and Pay by Check - only for Annual */}
					{selectedBillingCycle === 12 && (
						<div className={css(styleSheet.formGroup)}>
							<div className={css(styleSheet.checkboxGrid)}>
								<label className={css(styleSheet.checkboxLabel)}>
									<Checkbox checked={isSplitBilling} onCheckedChange={checked => setIsSplitBilling(checked === true)} />
									<span style={{ marginLeft: 8 }}>Split Billing</span>
								</label>
								<label className={css(styleSheet.checkboxLabel)}>
									<Checkbox checked={payByCheck} onCheckedChange={checked => setPayByCheck(checked === true)} />
									<span style={{ marginLeft: 8 }}>Pay by Check</span>
								</label>
							</div>
						</div>
					)}

					{/* Add-ons */}
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)}>Add-ons</label>
						<div className={css(styleSheet.checkboxGrid)}>
							<label
								className={css(styleSheet.checkboxLabel)}
								title={getAddOnUnavailableReason(metaAdsPremiumAvailable, aiProductSelected)}
							>
								<Checkbox
									checked={hasMetaAdsPremium}
									onCheckedChange={checked => setHasMetaAdsPremium(checked === true)}
									disabled={!metaAdsPremiumAvailable || aiProductSelected}
								/>
								<span style={{ marginLeft: 8 }}>Meta Ads Premium</span>
								{!metaAdsPremiumAvailable && !aiProductSelected && (
									<span className={css(styleSheet.unavailableNote)}>(not available)</span>
								)}
							</label>

							<label
								className={css(styleSheet.checkboxLabel)}
								title={getAddOnUnavailableReason(googleLSAAvailable, aiProductSelected)}
							>
								<Checkbox
									checked={hasGoogleLSA}
									onCheckedChange={checked => setHasGoogleLSA(checked === true)}
									disabled={!googleLSAAvailable || aiProductSelected}
								/>
								<span style={{ marginLeft: 8 }}>Google LSA</span>
								{!googleLSAAvailable && !aiProductSelected && (
									<span className={css(styleSheet.unavailableNote)}>(not available)</span>
								)}
							</label>

							<label
								className={css(styleSheet.checkboxLabel)}
								title={getAddOnUnavailableReason(googlePpcAvailable, aiProductSelected)}
							>
								<Checkbox
									checked={hasGooglePpc}
									onCheckedChange={checked => setHasGooglePpc(checked === true)}
									disabled={!googlePpcAvailable || aiProductSelected}
								/>
								<span style={{ marginLeft: 8 }}>Google PPC</span>
								{!googlePpcAvailable && !aiProductSelected && (
									<span className={css(styleSheet.unavailableNote)}>(not available)</span>
								)}
							</label>

							<label
								className={css(styleSheet.checkboxLabel)}
								title={getAddOnUnavailableReason(handwrittenCardsAvailable, aiProductSelected)}
							>
								<Checkbox
									checked={hasHandwrittenCards}
									onCheckedChange={checked => setHasHandwrittenCards(checked === true)}
									disabled={!handwrittenCardsAvailable || aiProductSelected}
								/>
								<span style={{ marginLeft: 8 }}>Handwritten Cards</span>
								{!handwrittenCardsAvailable && !aiProductSelected && (
									<span className={css(styleSheet.unavailableNote)}>(not available)</span>
								)}
							</label>
						</div>
					</div>

					<div className={css(styleSheet.buttonRow)}>
						<Button
							disabled={
								isCalculating || !selectedProduct || selectedDiscount === undefined || selectedDiscount === null
							}
							isLoading={isCalculating}
							type='submit'
							variant='primary'
						>
							Calculate
						</Button>
						<Button onClick={() => setIsCurrentContractModalOpen(true)} type='button' variant='primary'>
							Current Client Contract
						</Button>
					</div>
				</form>

				{/* Right: Results Display */}
				<div className={css(styleSheet.resultsSection)}>
					{isCalculating && (
						<div className={css(styleSheet.spinnerContainer)}>
							<LoadingSpinner type='small' />
						</div>
					)}

					{!isCalculating && !result && (
						<div className={css(styleSheet.resultsContent)}>
							<div className={css(styleSheet.placeholder)}>
								Select a product and click <em>&quot;Calculate&quot;</em> to see pricing
							</div>
						</div>
					)}

					{!isCalculating && result && pricebookItem && (
						<div className={css(styleSheet.resultsContent)}>
							<div className={css(styleSheet.breakdown)}>
								{renderResultRow('Base Price:', result.basePrice, { monthlyValue: result.basePriceMRR })}
								{renderResultRow('Extra User Cost:', result.extraUserCost, {
									monthlyValue: result.extraUserCostMRR,
								})}
								{renderResultRow('Onboarding Fee:', result.onboardingFee)}
								{pricebookItem.hasFacebookAdsPremium &&
									renderResultRow('Meta Ads Premium Fee:', result.facebookAdsFee, {
										shouldShow: true,
										monthlyValue: result.facebookAdsMRR,
									})}
								{pricebookItem.hasGoogleLocalServiceAds &&
									renderResultRow('Google LSA Fee:', result.googleLSAFee, {
										shouldShow: true,
										monthlyValue: result.googleLSAMRR,
									})}
								{pricebookItem.hasGooglePpc &&
									renderResultRow('Google PPC Fee:', result.googlePpcFee, {
										shouldShow: true,
										monthlyValue: result.googlePpcMRR,
									})}
								{pricebookItem.hasHandwrittenCards &&
									renderResultRow('$500 Handwritten Card Credit:', result.handwrittenCardsFee, {
										shouldShow: true,
										monthlyValue: result.handwrittenCardsMRR,
									})}
								{renderResultRow('Subtotal:', result.subtotal)}
								{(result.discountAmount ?? 0) > 0 &&
									renderResultRow(`Discount (${result.discountPercentage}%):`, result.discountAmount)}
								{renderResultRow('Total Before Tax:', result.totalBeforeTaxes)}
								<div className={css(styleSheet.resultRow)}>
									<span className={css(styleSheet.resultLabel)}>Estimated Tax Amount:</span>
									<span className={css(styleSheet.resultValue)}>
										<TaxEstimateDisplay
											taxAmount={taxEstimate?.taxAmount ?? null}
											onCalculateClick={() => setIsTaxModalOpen(true)}
											onEditClick={() => setIsTaxModalOpen(true)}
											formatCurrency={formatCurrency}
										/>
									</span>
								</div>
								{renderResultRow('Annual Total:', (result.totalBeforeTaxes ?? 0) + (taxEstimate?.taxAmount ?? 0))}
							</div>

							<div className={css(styleSheet.summaryRow)}>
								<div className={css(styleSheet.summaryCard)}>
									<div>
										<strong>Amount Due Today:</strong> ${formatCurrency(pricebookItem.totalAmountBilledToday)}
									</div>
									{pricebookItem.isSplitBilling && (
										<div className={css(styleSheet.splitBillingNote)}>
											The final installment payment of ${formatCurrency(pricebookItem.totalAmountBilledToday)} will be
											billed in 60 days.
										</div>
									)}
								</div>
								<div className={css(styleSheet.summaryCard)}>
									<div>
										<strong>MRR Amount:</strong> ${formatCurrency(pricebookItem.totalMRR)}
									</div>
									<div>
										<strong>Renewal - Annual:</strong> ${formatCurrency(result.renewalAnnualTotal)}
									</div>
									<div>
										<strong>Renewal - Quarterly:</strong> ${formatCurrency(result.renewalQuarterlyTotal)}
									</div>
								</div>
							</div>

							<div className={css(styleSheet.actionButtons)}>
								<Button onClick={() => setIsProposalModalOpen(true)} type='button' variant='primary'>
									📄 Generate Proposal
								</Button>
								<Button onClick={() => setIsNewContractModalOpen(true)} type='button' variant='primary'>
									📝 Generate Contract
								</Button>
								{pricebookItem.id && (
									<Button
										onClick={() => {
											const basePath = accountId
												? `/tools/collect-payment/${pricebookItem.id}/${accountId}`
												: `/tools/collect-payment/${pricebookItem.id}`;
											const params = new URLSearchParams();
											if (payByCheck) params.set('allowInvoice', 'true');
											if (taxEstimate) {
												params.set('tax', taxEstimate.taxAmount.toString());
												params.set(
													'taxAddr',
													JSON.stringify({
														address: taxEstimate.address,
														address2: taxEstimate.address2,
														city: taxEstimate.city,
														state: taxEstimate.state,
														zip: taxEstimate.zip,
													})
												);
											}
											const queryString = params.toString();
											const path = queryString ? `${basePath}?${queryString}` : basePath;
											history.push(path);
										}}
										type='button'
										variant='success'
									>
										💳 Collect Payment
									</Button>
								)}
								{pricebookItem.id && (
									<a
										className={css(styleSheet.publicPaymentLink)}
										href={`${import.meta.env.VITE_CHECKOUT_URL}#/${pricebookItem.id}${accountId ? `/${accountId}` : ''}`}
										rel='noopener noreferrer'
										target='_blank'
									>
										Public payment page
									</a>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Generate Proposal Modal */}
			{result && (
				<GenerateProposalModal
					isOpen={isProposalModalOpen}
					onRequestClose={() => setIsProposalModalOpen(false)}
					pricingData={result}
				/>
			)}

			{/* Generate New Client Contract Modal */}
			{result && (
				<GenerateContractModal
					contractType='new'
					isOpen={isNewContractModalOpen}
					onRequestClose={() => setIsNewContractModalOpen(false)}
					pricingData={result}
					isSuccessTeam={isSuccessTeam}
				/>
			)}

			{/* Generate Current Client Contract Modal */}
			<GenerateContractModal
				contractType='current'
				isOpen={isCurrentContractModalOpen}
				onRequestClose={() => setIsCurrentContractModalOpen(false)}
				isSuccessTeam={isSuccessTeam}
			/>

			{/* No Matches Found Modal */}
			<Modal isOpen={isNoMatchModalOpen} onRequestClose={() => setIsNoMatchModalOpen(false)} useDefaultHeader={true}>
				<div className={css(styleSheet.noMatchModal)}>
					<h2 className={css(styleSheet.noMatchTitle)}>No Matches Found</h2>
					<p className={css(styleSheet.noMatchMessage)}>
						No matching pricing configuration found for the selected options.
					</p>
					<a
						className={css(styleSheet.noMatchLink)}
						href='https://docs.google.com/forms/d/e/1FAIpQLSerFY52SyTRlNhKfLpwZzHbm5f1SGxALlaSxvpShosDFUJWRA/viewform'
						rel='noopener noreferrer'
						target='_blank'
					>
						Click here to request a custom link
					</a>
					<Button variant='secondary' onClick={() => setIsNoMatchModalOpen(false)}>
						Close
					</Button>
				</div>
			</Modal>

			{/* Tax Estimate Modal */}
			{pricebookItem && (
				<TaxEstimateModal
					isOpen={isTaxModalOpen}
					onRequestClose={() => setIsTaxModalOpen(false)}
					pricebookItemId={pricebookItem.id}
					initialData={taxEstimate ?? undefined}
					onSuccess={setTaxEstimate}
				/>
			)}
		</div>
	);
}
